import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { ScrollText } from "lucide-react";

export type TermSheetStatus =
  | "draft"
  | "internally_approved"
  | "sent"
  | "accepted"
  | "declined"
  | "countered";

const TERM_SHEET_STATUS_META: Record<
  TermSheetStatus,
  { label: string; tone: "neutral" | "info" | "warning" | "success" | "danger" | "primary" }
> = {
  draft:               { label: "Draft",          tone: "neutral" },
  internally_approved: { label: "Internal OK",    tone: "info" },
  sent:                { label: "Sent",           tone: "primary" },
  accepted:            { label: "Accepted",       tone: "success" },
  declined:            { label: "Declined",       tone: "danger" },
  countered:           { label: "Countered",      tone: "warning" },
};

const TONE_CLASSES: Record<string, string> = {
  neutral: "bg-slate-100 text-slate-700 border-slate-200",
  info:    "bg-blue-50 text-blue-800 border-blue-200",
  warning: "bg-amber-50 text-amber-800 border-amber-200",
  success: "bg-green-50 text-green-800 border-green-200",
  danger:  "bg-red-50 text-red-800 border-red-200",
  primary: "bg-purple-50 text-purple-800 border-purple-200",
};

/**
 * Compact "Term Sheet" pill — used on the contracts list and the
 * term-sheet detail page to communicate that this row is a non-binding
 * term sheet rather than a full contract. When `status` is supplied,
 * the pill also shows the candidate-facing term-sheet status (Draft,
 * Sent, Accepted, etc.) so the list communicates lifecycle in a single
 * glance.
 */
export function TermSheetBadge({
  status,
  className,
  size = "sm",
}: {
  status?: TermSheetStatus | null;
  className?: string;
  size?: "sm" | "md";
}) {
  const meta = status ? TERM_SHEET_STATUS_META[status] : null;
  return (
    <Badge
      variant="outline"
      className={cn(
        "font-medium gap-1 inline-flex items-center",
        meta ? TONE_CLASSES[meta.tone] : "bg-purple-50 text-purple-800 border-purple-200",
        size === "sm" ? "text-[10.5px] px-1.5 py-0.5" : "text-xs px-2 py-0.5",
        className,
      )}
      data-testid={`term-sheet-badge${status ? `-${status}` : ""}`}
      title={meta ? `Term sheet · ${meta.label}` : "Term sheet"}
    >
      <ScrollText className="h-3 w-3" />
      {meta ? `Term Sheet · ${meta.label}` : "Term Sheet"}
    </Badge>
  );
}
